import { motion } from "framer-motion";
import { Users, Cog, BadgeCheck, CircleDot } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const availabilityStyle = {
  available: "text-emerald-400 border-emerald-400/30 bg-emerald-400/10",
  on_request: "text-gold border-gold/30 bg-gold/10",
  unavailable: "text-slate-500 border-slate-500/30 bg-slate-500/10",
};

const VehicleCard = ({ vehicle, index, onOpen, onEnquire }) => {
  const { t } = useLanguage();

  return (
    <motion.article
      data-testid={`vehicle-card-${vehicle.id}`}
      layout
      initial={{ opacity: 0, y: 32 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.5, delay: Math.min(index * 0.05, 0.4), ease: [0.22, 1, 0.36, 1] }}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/[0.06] bg-panel transition-all duration-500 hover:border-gold/40 hover:shadow-[0_24px_70px_-24px_rgba(212,175,55,0.28)]"
    >
      <button
        type="button"
        data-testid={`vehicle-image-${vehicle.id}`}
        onClick={() => onOpen(vehicle)}
        className="relative block aspect-[16/10] w-full overflow-hidden"
        aria-label={`${t("fleet.viewDetails")} — ${vehicle.name}`}
      >
        <img
          src={vehicle.image}
          alt={`${vehicle.name} — ${t(`categories.${vehicle.category}`)} rental Dubai`}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-panel via-transparent to-transparent" />
        <span
          className={`absolute start-3 top-3 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-wider backdrop-blur-sm ${availabilityStyle[vehicle.availability] || availabilityStyle.on_request}`}
          data-testid={`vehicle-availability-${vehicle.id}`}
        >
          <CircleDot className="h-3 w-3" aria-hidden="true" />
          {t(`fleet.${vehicle.availability === "on_request" ? "onRequest" : vehicle.availability}`)}
        </span>
        {vehicle.no_deposit_eligible && (
          <span
            data-testid={`vehicle-nodeposit-${vehicle.id}`}
            className="absolute end-3 top-3 inline-flex items-center gap-1 rounded-full bg-gold px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[#08080A]"
          >
            <BadgeCheck className="h-3 w-3" aria-hidden="true" />
            {t("fleet.noDeposit")}
          </span>
        )}
      </button>

      <div className="flex flex-1 flex-col p-5">
        <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-gold/80">
          {t(`categories.${vehicle.category}`)}
        </p>
        <h3 className="mt-1.5 font-display text-2xl font-semibold text-white">{vehicle.name}</h3>

        <div className="mt-3 flex items-center gap-4 text-xs text-slate-400">
          <span className="inline-flex items-center gap-1.5">
            <Cog className="h-3.5 w-3.5 text-gold/70" aria-hidden="true" />
            {vehicle.transmission}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5 text-gold/70" aria-hidden="true" />
            {vehicle.seats} {t("fleet.seats")}
          </span>
        </div>

        <div className="mt-4 flex items-baseline gap-1.5 border-t border-white/[0.06] pt-4">
          {vehicle.price_per_day ? (
            <>
              <span className="font-display text-2xl font-bold text-gold-gradient" data-testid={`vehicle-price-${vehicle.id}`}>
                {vehicle.currency} {Number(vehicle.price_per_day).toLocaleString("en-US")}
              </span>
              <span className="text-xs text-slate-500">{t("fleet.perDay")}</span>
            </>
          ) : (
            <span className="text-sm font-medium text-slate-300" data-testid={`vehicle-price-${vehicle.id}`}>
              {t("fleet.priceOnRequest")}
            </span>
          )}
        </div>

        <div className="mt-4 flex gap-2.5">
          <button
            type="button"
            data-testid={`vehicle-details-${vehicle.id}`}
            onClick={() => onOpen(vehicle)}
            className="flex-1 rounded-full border border-gold/40 py-2.5 text-[11px] font-bold uppercase tracking-widest text-gold transition-all hover:bg-gold hover:text-[#08080A]"
          >
            {t("fleet.viewDetails")}
          </button>
          <button
            type="button"
            data-testid={`vehicle-enquire-${vehicle.id}`}
            onClick={() => onEnquire(vehicle)}
            className="flex-1 rounded-full bg-white/[0.06] py-2.5 text-[11px] font-bold uppercase tracking-widest text-white transition-all hover:bg-white/[0.12] hover:text-gold"
          >
            {t("fleet.enquire")}
          </button>
        </div>
      </div>
    </motion.article>
  );
};

export default VehicleCard;
